import Konva from "konva";
import { IRect } from "konva/lib/types";
import { COLLISION_MARGIN } from "./constants";
import { BBox, ImgProps } from "./types.d";
import { SceneContext } from 'konva/lib/Context';

/**
 * Find the closest point on the 'containWithin' shape to the center of
 * the 'rect' found within the 'containWithin' shape
 * version (1)
 */
export const calcClosestPoint = ({
  containWithin,
  clipOrigin,
  rect
}: {
  rect: IRect;
} & Pick<ImgProps, 'containWithin' | 'clipOrigin'>) => {
  const [clipX, clipY] = clipOrigin || [0, 0];
  const centeredRect = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
  const initial = {
    x: 0,
    y: 0,
    distance: Infinity,
    subject: centeredRect,
    index: -1
  };
  return containWithin?.reduce((sum: typeof initial, [tempX, tempY]: number[], index) => {
    const pointX = tempX + clipX;
    const pointY = tempY + clipY;
    const diffX = pointX - centeredRect.x;
    const diffY = pointY - centeredRect.y;
    const distance = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2));

    if (distance < sum.distance) {
      sum.distance = distance;
      sum.x = pointX;
      sum.y = pointY;
      sum.index = index;
    }

    return sum;
  }, initial) || initial;
};

/**
 * Find the closest point on the 'containWithin' shape to the center of
 * the 'rect' found within the 'containWithin' shape
 * version (2)
 */
export const calcClosestPoint2 = ({
  containWithin,
  clipOrigin,
  rect
}: {
  rect: IRect;
} & Pick<ImgProps, 'containWithin' | 'clipOrigin'>) => {
  const [clipX, clipY] = clipOrigin || [0, 0];
  const centeredRect = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
  const initial = {
    x: 0,
    y: 0,
    distance: Infinity,
    subject: centeredRect,
    index: -1
  };
  return containWithin?.reduce((sum: typeof initial, _point: number[], index: number, all: number[][]) => {
    const prevIndex = prevRollingIndex(index, all);
    const nextIndex = nextRollingIndex(index, all);
    const P = [centeredRect.x, centeredRect.y];
    const pointB = [all[prevIndex][0] + clipX, all[prevIndex][1] + clipY];
    const pointC = [all[nextIndex][0] + clipX, all[nextIndex][1] + clipY];
    const distance = Math.abs((pointC[0] - pointB[0]) * (pointB[1] - P[1]) - (pointB[0] - P[0]) * (pointC[1] - pointB[1])) / Math.sqrt(Math.pow(pointC[0] - pointB[0], 2) + Math.pow(pointC[1] - pointB[1], 2));

    if (distance < sum.distance) {
      sum.distance = distance;
      sum.x = (pointB[0] + pointC[0]) / 2;
      sum.y = (pointB[1] + pointC[1]) / 2;
      sum.index = index;
    }

    return sum;
  }, initial) || initial;
};

export function prevRollingIndex(index: number, list: number[][]): number {
  return (index === 0 ? list.length : index) - 1;
}

export function nextRollingIndex(index: number, all: number[][]) {
  return index === all.length - 1 ? 0 : index + 1;
}

export const containPointInsideBox = (
  bbox: BBox, point: { x: number; y: number }, clipOrigin: number[], margin: number = COLLISION_MARGIN
) => {
  const xLeft = bbox.left + clipOrigin[0] + margin;
  const yTop = bbox.top + clipOrigin[1] + margin;
  const xRight = bbox.right + clipOrigin[0] - margin;
  const yBottom = bbox.bottom + clipOrigin[1] - margin;
  // top left
  if (point.x < xLeft && point.y < yTop) {
    return { axis: 'xy', x: xLeft, y: yTop };
  }
  // top right
  if (point.x > xRight && point.y < yTop) {
    return { axis: 'xy', x: xRight, y: yTop };
  }
  // bottom right
  if (point.x > xRight && point.y > yBottom) {
    return { axis: 'xy', x: xRight, y: yBottom };
  }
  // bottom left
  if (point.x < xLeft && point.y > yBottom) {
    return { axis: 'xy', x: xLeft, y: yBottom };
  }
  // top
  if (point.y < yTop) {
    return { axis: 'y', y: yTop };
  }
  // right
  if (point.x > xRight) {
    return { axis: 'x', x: xRight };
  }
  // bottom
  if (point.y > yBottom) {
    return { axis: 'y', y: yBottom };
  }
  // left
  if (point.x < xLeft) {
    return { axis: 'x', x: xLeft };
  }

  return;
};

export function containWithinFaceplateBoundingBox({
  rect,
  faceplateBbox,
  clipOrigin,
  collisionMargin,
  node
}: {
  rect: IRect;
  faceplateBbox: BBox;
  clipOrigin: number[] | undefined;
  collisionMargin: number | undefined;
  node: Konva.Node;
}) {
  const subject = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
  const res = containPointInsideBox(faceplateBbox, subject, clipOrigin || [0, 0], collisionMargin);
  const newX = res?.x ? res?.x - (rect.width / 2 - (node.x() - rect.x)) : 0;
  const newY = res?.y ? res?.y - (rect.height / 2 - (node.y() - rect.y)) : 0;

  if (res?.axis === 'xy' && res?.x && res?.y) {
    node.x(newX);
    node.y(newY);
  } else if (res?.axis === 'x' && res?.x) {
    node.x(newX);
  } else if (res?.axis === 'y' && res?.y) {
    node.y(newY);
  }
}

export function layerClippingHandler(
  clip: number[][],
  clipOrigin: number[],
  layer: Partial<ImgProps>
): ((ctx: SceneContext) => void) | undefined {
  return (ctx: SceneContext | CanvasRenderingContext2D) => {
    if (!clip)
      return;
    ctx.moveTo(clip[0][0] + clipOrigin[0], clip[0][1] + clipOrigin[1]);
    clip.forEach((point: number[], index: number) => {
      const x = point[0] + (clipOrigin[0] || 0);
      const y = point[1] + (clipOrigin[1] || 0);
      const nextX = (clip[index + 1] ? clip[index + 1][0] : clip[0][0]) + clipOrigin[0];
      const nextY = (clip[index + 1] ? clip[index + 1][1] : clip[0][1]) + clipOrigin[1];
      if (layer.smoothClip === false) {
        ctx.lineTo(x, y);
      } else {
        ctx.quadraticCurveTo(x, y, (x + nextX) / 2, (y + nextY) / 2);
      }
    });
    if (layer.debugBoundaries) {
      // @ts-ignore
      ctx.strokeStyle = "#FF0F00";
      // @ts-ignore
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  };
}
